/**
 * The list switch: pausing a rule, and turning one on — which, for a rule that
 * applies to past, is counted and confirmed first (Review Focus 3).
 *
 * It has state of its own. It used to share `step` and `offer` with the
 * builder's save, so answering the switch's dialog ran `reset`, which ended a
 * builder session that had started while the switch counted (#505).
 * `via` on `switchOffer` is always 'enable'.
 */
import { useRef, useState } from 'react'
import { clampStart, localToday, windowStart } from './backfillWindow'
import {
  categoryOf,
  IDLE,
  mayHaveCommitted,
  ruleSwitchedToast,
  toggleFailed,
  track,
  type ConfirmStep,
  type Offer,
  type SaveStep,
} from './saveShared'
import type { RuleBase } from './useRuleBase'
import type { ReadRule } from './ruleWire'
import { fetchBackfillPreview, type SaveResult } from './useRules'
import { keys } from '../../api/keys'

export function useRuleSwitch(base: RuleBase) {
  const { client, update, toast, announceBackfill } = base
  const [switchStep, setSwitchStep] = useState<SaveStep>(IDLE)
  const [switchOffer, setSwitchOffer] = useState<Offer | null>(null)
  const [switching, setSwitching] = useState<ReadonlySet<string>>(new Set())
  /** Synchronous: state lags a double click by one render. */
  const inFlight = useRef(new Set<string>())

  function resetSwitch() {
    setSwitchStep(IDLE)
    setSwitchOffer(null)
  }

  /** Step 4 from the switch — and "Try again" after it failed. */
  async function confirmEnable(o: Offer) {
    setSwitchStep({ k: 'applying' })
    let result: SaveResult
    try {
      result = await update.mutateAsync({ id: o.id, body: { is_enabled: true } })
    } catch (e) {
      if (mayHaveCommitted(e)) void client.invalidateQueries({ queryKey: keys.rules.all() })
      toast.show({ kind: 'error', message: toggleFailed(o.name, e) })
      resetSwitch()
      return
    }
    // The write has settled: nothing below can turn it into a failure.
    track('rule_backfill_confirmed', { transaction_count: o.count, at_least_shown: o.atLeast })
    track('rule_enabled', { apply_to_past_enabled: true })
    // A batch that changed rows says so; one that changed none still turned the rule on.
    if (!announceBackfill(result)) toast.show({ kind: 'success', message: ruleSwitchedToast(result.rule.rule.name, true) })
    resetSwitch()
  }

  /** The rule is turned on with the flag off: nothing more is written to history. */
  function skipEnable(o: Offer) {
    resetSwitch()
    void patchSwitch(o.id, o.name, { is_enabled: true, apply_to_past: false })
  }

  async function patchSwitch(id: string, name: string, body: { is_enabled: boolean; apply_to_past?: boolean }) {
    if (inFlight.current.has(id)) return
    inFlight.current.add(id)
    setSwitching(new Set(inFlight.current))
    let result: SaveResult
    try {
      result = await update.mutateAsync({ id, body })
    } catch (e) {
      // No optimistic write to roll back: the switch reads the cache, which
      // still holds the server's state — unless the PATCH committed unheard, so re-read.
      if (mayHaveCommitted(e)) void client.invalidateQueries({ queryKey: keys.rules.all() })
      toast.show({ kind: 'error', message: toggleFailed(name, e) })
      return
    } finally {
      inFlight.current.delete(id)
      setSwitching(new Set(inFlight.current))
    }
    track(body.is_enabled ? 'rule_enabled' : 'rule_paused', {
      apply_to_past_enabled: result.rule.rule.apply_to_past,
    })
    // iOS toasts every flip (RuleCopy.enabledToast / disabledToast). A batch that
    // changed rows says so instead: one toast per flip, so it is announced once.
    if (!announceBackfill(result)) {
      const on = body.is_enabled
      toast.show({ kind: 'success', message: ruleSwitchedToast(result.rule.rule.name, on) })
    }
  }

  /** The list's switch turning a rule OFF. */
  function pause(read: ReadRule) {
    void patchSwitch(read.rule.id, read.rule.name, { is_enabled: false })
  }

  /** The list's switch turning a rule ON (Review Focus 3). One that applies to
   *  past would backfill the moment it is enabled, so it is counted and
   *  confirmed first; anything else is a plain PATCH. */
  async function enable(read: ReadRule) {
    const { id, name, apply_to_past: past } = read.rule
    if (!past) return patchSwitch(id, name, { is_enabled: true })
    if (inFlight.current.has(id)) return
    inFlight.current.add(id)
    setSwitching(new Set(inFlight.current))
    setSwitchStep({ k: 'counting' })
    const today = localToday()
    const start = clampStart(read.rule.backfill_start_date ?? null, today)
    let counted: Awaited<ReturnType<typeof fetchBackfillPreview>>
    try {
      counted = await fetchBackfillPreview(id, start)
    } catch {
      toast.show({ kind: 'error', message: toggleFailed(name) })
      setSwitchStep(IDLE)
      return
    } finally {
      inFlight.current.delete(id)
      setSwitching(new Set(inFlight.current))
    }
    if (counted.count === 0) {
      setSwitchStep(IDLE)
      return patchSwitch(id, name, { is_enabled: true })
    }
    const next: ConfirmStep = {
      k: 'confirm',
      id,
      ...counted,
      from: windowStart(start, today),
      defaulted: start === null,
    }
    setSwitchOffer({ ...next, via: 'enable', raised: base.raised(), name, categoryId: categoryOf(read), start })
    setSwitchStep(next)
  }

  return {
    switchStep,
    switchOffer,
    switching,
    pause,
    enable,
    confirmEnable,
    skipEnable,
    resetSwitch,
  }
}
